import { Typography } from '@workspace/ui/components/Typography';
import { ChevronRight16, Copy12, QrCode24 } from '@workspace/ui/icons';
import { cn } from '@workspace/ui/lib/utils';
import { differenceInDays } from 'date-fns';
import { format } from 'date-fns-tz';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import type { OrderShort } from '~/api/Order';
import { company } from '~/company/Company';
import { cfSrcForImage } from '~/components/CategoryBlocks/utils';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '~/components/isolated/Carousel';
import { IconButton } from '~/components/isolated/IconButton';
import { ModalClientCard } from '~/components/ModalClientCard';
import { useDateLang } from '~/hooks/useDateLang';
import { useTimeFormat } from '~/hooks/useTimeFormat';
import {
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';
import { normalizeOrderId } from '~/utils/normalizeOrderId';

const UPSCALED_IMAGE_SIZE = 48 * 3;

const getUpscaledImagePath = (image: string) => {
  return cfSrcForImage(image, {
    width: UPSCALED_IMAGE_SIZE,
    height: UPSCALED_IMAGE_SIZE,
  });
};

interface ActiveOrdersSectionProps
  extends Omit<React.ComponentPropsWithoutRef<'section'>, 'children'> {
  activeOrders: OrderShort[];
  name: string;
  phone: string;
}

export const ActiveOrdersSection: React.FC<ActiveOrdersSectionProps> = ({
  activeOrders,
  name,
  phone,
  className,
  ...props
}) => {
  const [isClientCardOpen, setIsClientCardOpen] = useState(false);
  const handleCloseClientCard = useCallback(
    () => setIsClientCardOpen(false),
    [],
  );

  return (
    <section
      {...props}
      className={cn(
        '-mx-4 overflow-hidden bg-[radial-gradient(150%_130%_at_-10%_80%,var(--graphic-regular-brand)_-80%,transparent_100%)] px-4 pt-safe-offset-4 pb-8',
        className,
      )}
    >
      {company.images.profileBackground && (
        <div
          style={{
            backgroundImage: `url(${company.images.profileBackground})`,
          }}
          className="absolute -top-2 -right-[31px] h-[124px] w-[173px] bg-center bg-contain mix-blend-luminosity"
        />
      )}

      <div className="relative flex items-center justify-between pb-4">
        <div className="flex flex-col">
          <Typography.Header200Medium className="py-1 text-text-primary">
            {name}
          </Typography.Header200Medium>
          <Typography.Text200Regular className="text-text-secondary">
            {phone}
          </Typography.Text200Regular>
        </div>
        <IconButton
          size="L"
          variant="shadowXS"
          onClick={() => setIsClientCardOpen(true)}
        >
          <QrCode24 className="text-icon-brand" />
        </IconButton>
        <ModalClientCard
          isOpen={isClientCardOpen}
          onClose={handleCloseClientCard}
          isFullHeight
        />
      </div>

      {activeOrders.length > 0 && (
        <Carousel
          opts={{
            align: 'start' as const,
            slidesToScroll: 1,
            containScroll: 'trimSnaps' as const,
          }}
          className="py-6"
        >
          <CarouselContent className="overscreen-4! overflow-visible!">
            {activeOrders.map((order) => (
              <CarouselItem
                key={order.id}
                className="mr-2 rounded-5 pl-0! shadow-md"
              >
                <OrderItem order={order} />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
      )}
    </section>
  );
};

interface OrderItemProps {
  order: OrderShort;
  className?: string;
}

const OrderItem: React.FC<OrderItemProps> = ({ order, className }) => {
  const { t } = useTranslation();
  const { locale } = useDateLang();
  const { timeFormat } = useTimeFormat();

  const normalizedOrderId = normalizeOrderId(order.order_id);

  const estimatedTime = useMemo(() => {
    const estimationDateMin = new Date(order.sla.deliver_by_min);
    const estimationDateMax = new Date(order.sla.deliver_by_max);
    const difference = differenceInDays(new Date(), estimationDateMin);

    const capitalize = (str: string) =>
      str.charAt(0).toUpperCase() + str.slice(1);

    if (!difference) {
      return capitalize(
        t('phrases:todayAt', {
          timeMin: format(estimationDateMin, timeFormat, {
            timeZone: company.config.timeZone,
            locale,
          }),
          timeMax: format(estimationDateMax, timeFormat, {
            timeZone: company.config.timeZone,
            locale,
          }),
        }),
      );
    }

    return capitalize(
      t('phrases:deliveryTime', {
        date: format(estimationDateMin, 'MM/dd/yyyy', {
          timeZone: company.config.timeZone,
          locale,
        }),
        timeMin: format(estimationDateMin, timeFormat, {
          timeZone: company.config.timeZone,
          locale,
        }),
        timeMax: format(estimationDateMax, timeFormat, {
          timeZone: company.config.timeZone,
          locale,
        }),
      }),
    );
  }, [order, t, locale, timeFormat]);

  const formattedTotal = formatPriceWithCurrency(
    convertPenceToPounds(order.paid_total || 0),
  );

  const handleCopyOrderId = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(normalizedOrderId);
  };

  return (
    <Link
      to={`/order/${order.id}`}
      className={cn(
        'flex gap-3 overflow-hidden rounded-[inherit] bg-bg-base',
        className,
      )}
    >
      <div className="grid shrink-0 grid-cols-[repeat(2,48px)] grid-rows-[repeat(2,48px)] gap-[2px] overflow-hidden rounded-r-4">
        {order.items
          .filter((item) => item.image)
          .slice(0, 4)
          .map((item) => (
            <img
              key={item.id}
              src={getUpscaledImagePath(item.image)}
              alt={item.name}
              className="h-full w-full bg-surface-transparent-2 object-contain"
            />
          ))}
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1 self-stretch pt-2 pr-2">
        <div className="flex flex-col gap-1.5">
          <div className="flex items-start justify-between">
            <Typography.Text200Medium className="truncate text-text-primary">
              {estimatedTime}
            </Typography.Text200Medium>
            <ChevronRight16 className="shrink-0 text-icon-primary" />
          </div>

          <div className="inline-flex">
            <Typography.Text50Medium className="rounded-full bg-system-success-subtle px-1.5 py-0.5 text-system-success">
              {t(`orderStatus:${order.public_status}.value`)}
            </Typography.Text50Medium>
          </div>
        </div>

        <Typography.Text100Medium className="truncate py-0.5 text-text-secondary">
          {order.address?.address_1}
        </Typography.Text100Medium>

        <div className="flex items-end justify-between">
          <Typography.Text100Semibold className="truncate text-text-primary">
            {formattedTotal}
          </Typography.Text100Semibold>

          <button
            type="button"
            onClick={handleCopyOrderId}
            className="flex shrink-0 cursor-pointer items-center gap-1 border-none bg-transparent pr-0.5 outline-none active:opacity-60"
            aria-label={t('phrases:orderNumber', { number: normalizedOrderId })}
          >
            <Typography.Text100Medium className="truncate text-text-tertiary">
              #{normalizedOrderId}
            </Typography.Text100Medium>
            <Copy12 className="text-icon-tertiary" />
          </button>
        </div>
      </div>
    </Link>
  );
};
